/**
 * Learn Melodies Page
 *
 * Comprehensive guide to understanding and training melodic dictation.
 * Covers theory, practical applications, and how to use the Melodies Drill tool.
 */

import React from "react";
import { Link } from "react-router-dom";
import {
    LearnPageLayout,
    ContentSection,
    CalloutBox,
    OutcomeItem,
} from "../../components/learn";
import { ROUTES } from "../../config/routes";
import { SEO_METADATA } from "../../config/seoMetadata";
import SEO from "../../components/SEO";
import styles from "./StartHere.module.css";

const tocItems = [
    { id: "what-are-melodies", label: "What is melodic dictation?" },
    { id: "why-melodies-matter", label: "Why melodies matter" },
    { id: "scale-degrees", label: "Scale degrees" },
    { id: "scale-types", label: "Major and minor scales" },
    { id: "melodic-patterns", label: "Common melodic patterns" },
    { id: "recognizing-melodies", label: "Recognizing melodies" },
    { id: "using-the-drill", label: "Using the melodies drill" },
    { id: "practice-strategies", label: "Practice strategies" },
];

export default function MelodiesLearn() {
    return (
        <>
            <SEO {...SEO_METADATA.LEARN_MELODIES} />
            <LearnPageLayout
                title="Learn Melodies"
                subtitle="Master the art of hearing and transcribing musical lines"
                tocItems={tocItems}
        >
            {/* What is Melodic Dictation */}
            <ContentSection id="what-are-melodies">
                <h2 className={styles.sectionTitle}>What is melodic dictation?</h2>

                <p>
                    <strong>Melodic dictation</strong> is the skill of hearing a melody and
                    identifying the sequence of notes being played. While{" "}
                    <Link to={ROUTES.LEARN.INTERVALS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                        intervals
                    </Link>{" "}
                    teach you to recognize the distance between two notes, melodic training teaches
                    you to hear entire musical phrases and understand their structure.
                </p>

                <p>
                    Think of it this way: if intervals are like learning individual words, melodic
                    dictation is like understanding full sentences. You're hearing how notes move
                    in sequence, creating meaning through their journey up and down the scale.
                </p>

                <CalloutBox>
                    <p>
                        <strong>Key insight:</strong> The most effective way to recognize melodies
                        is by <strong>scale degrees</strong>—the position of each note within the
                        key's scale. Instead of thinking "that's a G," you think "that's the 5th
                        note of the scale." This makes melodies transferable across all keys.
                    </p>
                </CalloutBox>

                <p>
                    When you hear a melody, your trained ear should recognize patterns like
                    "1-2-3-5" or "5-4-3-2-1"—sequences that show up in countless songs across all
                    genres. This is the language of melody.
                </p>
            </ContentSection>

            {/* Why Melodies Matter */}
            <ContentSection id="why-melodies-matter">
                <h2 className={styles.sectionTitle}>Why melodic training matters</h2>

                <p>
                    Melodic dictation is one of the most practical ear training skills. When you
                    can hear and identify melodies, you unlock:
                </p>

                <div style={{ marginTop: "1.5rem" }}>
                    <OutcomeItem icon="🎵" title="Play any melody by ear">
                        Hear a tune once and immediately know which notes to play, no sheet music
                        needed.
                    </OutcomeItem>
                    <OutcomeItem icon="✍️" title="Transcribe solos and licks">
                        Write down that amazing guitar solo, vocal run, or instrumental melody you
                        just heard.
                    </OutcomeItem>
                    <OutcomeItem icon="🎹" title="Improvise melodically">
                        Know where you are in the scale at all times, making your improvisations
                        intentional and musical.
                    </OutcomeItem>
                    <OutcomeItem icon="🎼" title="Compose memorable melodies">
                        Understand which melodic patterns work and why, crafting catchy hooks and
                        phrases.
                    </OutcomeItem>
                    <OutcomeItem icon="🎤" title="Sing accurately in tune">
                        Develop your internal pitch reference, improving your vocal intonation and
                        pitch matching.
                    </OutcomeItem>
                </div>
            </ContentSection>

            {/* Scale Degrees */}
            <ContentSection id="scale-degrees">
                <h2 className={styles.sectionTitle}>Understanding scale degrees</h2>

                <p>
                    In any key, there are seven notes in the scale, numbered 1 through 7. These
                    are called <strong>scale degrees</strong>. Each degree has a unique sound and
                    function within the key:
                </p>

                <div className={styles.scaleDegreesTable}>
                    <table style={{ width: "100%", marginTop: "1rem", borderCollapse: "collapse" }}>
                        <thead>
                            <tr style={{ borderBottom: "2px solid var(--text-color)", textAlign: "left" }}>
                                <th style={{ padding: "0.75rem" }}>Degree</th>
                                <th style={{ padding: "0.75rem" }}>Name</th>
                                <th style={{ padding: "0.75rem" }}>In C Major</th>
                                <th style={{ padding: "0.75rem" }}>Character/Feeling</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>1 (Do)</strong></td>
                                <td style={{ padding: "0.75rem" }}>Tonic</td>
                                <td style={{ padding: "0.75rem" }}>C</td>
                                <td style={{ padding: "0.75rem" }}>Home, stable, resolved</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>2 (Re)</strong></td>
                                <td style={{ padding: "0.75rem" }}>Supertonic</td>
                                <td style={{ padding: "0.75rem" }}>D</td>
                                <td style={{ padding: "0.75rem" }}>Neutral, moving upward</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>3 (Mi)</strong></td>
                                <td style={{ padding: "0.75rem" }}>Mediant</td>
                                <td style={{ padding: "0.75rem" }}>E</td>
                                <td style={{ padding: "0.75rem" }}>Bright, defining major/minor quality</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>4 (Fa)</strong></td>
                                <td style={{ padding: "0.75rem" }}>Subdominant</td>
                                <td style={{ padding: "0.75rem" }}>F</td>
                                <td style={{ padding: "0.75rem" }}>Slightly tense, wants to resolve</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>5 (Sol)</strong></td>
                                <td style={{ padding: "0.75rem" }}>Dominant</td>
                                <td style={{ padding: "0.75rem" }}>G</td>
                                <td style={{ padding: "0.75rem" }}>Strong, stable, like a second home</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>6 (La)</strong></td>
                                <td style={{ padding: "0.75rem" }}>Submediant</td>
                                <td style={{ padding: "0.75rem" }}>A</td>
                                <td style={{ padding: "0.75rem" }}>Dark, emotional, introspective</td>
                            </tr>
                            <tr>
                                <td style={{ padding: "0.75rem" }}><strong>7 (Ti)</strong></td>
                                <td style={{ padding: "0.75rem" }}>Leading Tone</td>
                                <td style={{ padding: "0.75rem" }}>B</td>
                                <td style={{ padding: "0.75rem" }}>Tense, wants to resolve up to 1</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Why scale degrees matter</h3>
                <p>
                    Scale degrees are universal. The melody "1-3-5-3-1" sounds the same
                    emotionally whether you play it in C Major, G Major, or any other key. The
                    absolute pitches change, but the <em>relationships</em> stay the same.
                </p>

                <p>
                    This is incredibly powerful: once you can hear scale degrees, you can play any
                    melody in any key instantly. You're not memorizing specific pitches—you're
                    hearing patterns.
                </p>

                <CalloutBox>
                    <p>
                        <strong>Fun fact:</strong> The solfège syllables (Do, Re, Mi, Fa, Sol, La,
                        Ti, Do) from "The Sound of Music" are a system for singing scale degrees.
                        Many musicians use solfège when practicing melodic dictation!
                    </p>
                </CalloutBox>
            </ContentSection>

            {/* Scale Types */}
            <ContentSection id="scale-types">
                <h2 className={styles.sectionTitle}>Major and minor scales</h2>

                <p>
                    Not all scales sound the same. The three most common scale types each have a
                    distinct character:
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Major scale</h3>
                <p>
                    The <strong>major scale</strong> is the bright, happy scale. It's the "Do Re
                    Mi Fa Sol La Ti Do" you know. The pattern of whole and half steps creates its
                    characteristic sound.
                </p>
                <p style={{ fontStyle: "italic", color: "var(--text-secondary)" }}>
                    Pattern: W-W-H-W-W-W-H (W = whole step, H = half step)
                </p>
                <p>
                    <strong>Examples:</strong> "Happy Birthday," "Twinkle Twinkle Little Star,"
                    "Amazing Grace"
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Natural minor scale</h3>
                <p>
                    The <strong>natural minor scale</strong> has a darker, sadder quality. The 3rd,
                    6th, and 7th scale degrees are lowered by a half step compared to major.
                </p>
                <p style={{ fontStyle: "italic", color: "var(--text-secondary)" }}>
                    Pattern: W-H-W-W-H-W-W
                </p>
                <p>
                    <strong>Examples:</strong> "Losing My Religion" (R.E.M.), "Stairway to Heaven"
                    verse (Led Zeppelin)
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Harmonic minor scale</h3>
                <p>
                    The <strong>harmonic minor scale</strong> is like natural minor, but with a
                    raised 7th degree. This creates an exotic, Middle Eastern sound with a
                    distinctive gap between the 6th and 7th degrees.
                </p>
                <p style={{ fontStyle: "italic", color: "var(--text-secondary)" }}>
                    Pattern: W-H-W-W-H-W+H-H (the W+H is a step and a half)
                </p>
                <p>
                    <strong>Examples:</strong> Classical music, flamenco, klezmer, metal, and
                    dramatic film scores
                </p>

                <CalloutBox>
                    <p>
                        <strong>Start simple:</strong> Begin your melodic training with just the
                        major scale. Once you're comfortable identifying major scale degree
                        patterns, gradually add natural minor and harmonic minor.
                    </p>
                </CalloutBox>
            </ContentSection>

            {/* Common Melodic Patterns */}
            <ContentSection id="melodic-patterns">
                <h2 className={styles.sectionTitle}>Common melodic patterns</h2>

                <p>
                    Certain scale degree patterns appear over and over in music. Learning to
                    recognize these common patterns makes melodic dictation much easier:
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Ascending and descending scales</h3>
                <p>
                    <strong>1-2-3-4-5</strong> or <strong>5-4-3-2-1</strong> — Simple stepwise
                    motion up or down. Very common in classical and folk music.
                </p>
                <p><strong>Example:</strong> "Do-Re-Mi" from The Sound of Music goes 1-2-3-4-5</p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Arpeggios (chord outlines)</h3>
                <p>
                    <strong>1-3-5</strong> — Outlines a major chord. Sounds confident and stable.
                </p>
                <p><strong>Example:</strong> "Star Spangled Banner" starts with 1-3-5</p>
                <p>
                    <strong>1-3-5-8</strong> (8 being the octave) — Common in military and patriotic
                    songs, fanfares.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Neighbor tones</h3>
                <p>
                    <strong>1-2-1</strong> or <strong>5-6-5</strong> — A note moves up or down by
                    one step and returns. Creates a sense of ornamentation.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Leaps and returns</h3>
                <p>
                    <strong>1-5-3</strong> — Jump up, then partially back down. Very common opening
                    gesture.
                </p>
                <p><strong>Example:</strong> "Twinkle Twinkle Little Star" — 1-1-5-5-6-6-5</p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Cadential patterns</h3>
                <p>
                    <strong>2-1</strong> or <strong>7-8</strong> — Strong sense of resolution,
                    ending a phrase.
                </p>
                <p>
                    <strong>5-1</strong> — Very common ending, leap down from dominant to tonic.
                </p>

                <CalloutBox>
                    <p>
                        <strong>Practice tip:</strong> Sing these patterns in different keys. Hum
                        them. Play them on your instrument. The more familiar they become, the
                        easier it will be to recognize them when you hear them in real melodies.
                    </p>
                </CalloutBox>
            </ContentSection>

            {/* Recognizing Melodies */}
            <ContentSection id="recognizing-melodies">
                <h2 className={styles.sectionTitle}>How to recognize melodies</h2>

                <p>
                    Melodic dictation is challenging because it requires multiple skills working
                    together. Here's how to develop this ability:
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>1. Always establish the tonic first</h3>
                <p>
                    Before trying to identify any melody, you need to know where "home" (scale
                    degree 1) is. Most melodies start or end on the tonic. Play or sing the
                    reference note repeatedly until it's anchored in your mind.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>2. Sing it back</h3>
                <p>
                    After hearing a melody, immediately try to sing or hum it back. This engages
                    your vocal cords and reinforces the melodic contour in your memory. Don't
                    worry about perfection—just get close.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>3. Notice the contour first</h3>
                <p>
                    Before identifying specific scale degrees, notice the shape: Does the melody go
                    up? Down? Stay mostly the same? Does it leap or move stepwise? Understanding
                    the overall contour narrows down the possibilities.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>4. Identify stable notes</h3>
                <p>
                    Scale degrees 1, 3, and 5 sound stable and resolved. If a note in the melody
                    feels like "home," it's probably scale degree 1. If it feels stable but not
                    quite home, it might be 3 or 5. Use these anchor points to figure out the rest.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>5. Look for patterns you know</h3>
                <p>
                    Does the melody start with 1-3-5? Does it have that 5-4-3-2-1 descending scale
                    pattern? Recognizing common patterns dramatically speeds up melodic
                    identification.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>6. Break it into chunks</h3>
                <p>
                    Don't try to process the whole melody at once. Break it into smaller segments:
                    first identify the first 2-3 notes, then the next 2-3 notes, and so on. Like
                    reading a long word syllable by syllable.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>7. Use your interval knowledge</h3>
                <p>
                    If you've trained your ear for{" "}
                    <Link to={ROUTES.LEARN.INTERVALS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                        intervals
                    </Link>
                    , use that skill! If you hear a Perfect 5th leap, you know you've jumped from
                    scale degree 1 to 5 (or 5 to 2, etc.). Combine interval recognition with scale
                    degree thinking.
                </p>

                <CalloutBox>
                    <p>
                        <strong>Be patient:</strong> Melodic dictation is one of the most advanced
                        ear training skills. It takes time and consistent practice. If you can get
                        even the first few notes of a melody, that's progress! Celebrate small wins
                        and keep practicing.
                    </p>
                </CalloutBox>
            </ContentSection>

            {/* Using the Drill */}
            <ContentSection id="using-the-drill">
                <h2 className={styles.sectionTitle}>Using the melodies drill</h2>

                <p>
                    Ready to train your ear for melodies? The{" "}
                    <Link to={ROUTES.DRILL.MELODIES} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                        Melodies Drill
                    </Link>{" "}
                    generates random melodic sequences for you to identify by scale degrees.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>How the drill works</h3>
                <ol style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>
                        <strong>Configure your settings:</strong>
                        <ul style={{ marginLeft: "1.5rem", marginTop: "0.5rem" }}>
                            <li>
                                <strong>Scale degrees:</strong> Choose which degrees to include in
                                melodies. Start with just 1, 2, 3, and 5.
                            </li>
                            <li>
                                <strong>Melody length:</strong> 4, 6, or 8 notes. Start with 4 notes
                                for easier recognition.
                            </li>
                            <li>
                                <strong>Scale type:</strong> Major, Natural Minor, or Harmonic Minor.
                                Start with just Major.
                            </li>
                            <li>
                                <strong>Reference note:</strong> The key the melodies will be played
                                in (usually C).
                            </li>
                            <li>
                                <strong>Difficulty:</strong> Easy (very different answer options),
                                Medium, or Hard (very similar options).
                            </li>
                        </ul>
                    </li>
                    <li>
                        <strong>Choose a practice mode:</strong> Questions, Time, or Endless mode
                    </li>
                    <li>
                        <strong>Start the drill:</strong> You'll hear a short melody played
                    </li>
                    <li>
                        <strong>Identify the scale degrees:</strong> Select which sequence of numbers
                        matches what you heard
                    </li>
                    <li>
                        <strong>Get feedback:</strong> Instantly see if you're correct, with the
                        option to try again
                    </li>
                    <li>
                        <strong>Track progress:</strong> Review your accuracy at the end
                    </li>
                </ol>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Key features</h3>
                <ul style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>
                        <strong>Reference note button:</strong> Play the tonic (scale degree 1) as
                        many times as you need to establish your reference pitch
                    </li>
                    <li>
                        <strong>Replay melody:</strong> Listen to the melody multiple times before
                        answering
                    </li>
                    <li>
                        <strong>Instrument selection:</strong> Practice with different timbres to
                        develop robust melodic recognition
                    </li>
                    <li>
                        <strong>Volume control:</strong> Adjust to your comfort level
                    </li>
                </ul>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Recommended starting settings</h3>
                <p>For beginners, we recommend:</p>
                <ul style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>Scale degrees: 1, 2, 3, 5 only</li>
                    <li>Melody length: 4 notes</li>
                    <li>Scale type: Major only</li>
                    <li>Difficulty: Easy</li>
                    <li>Mode: Time (30-60 seconds) or Questions (5-10 questions)</li>
                </ul>

                <CalloutBox>
                    <p>
                        <strong>Ready to start?</strong> Head to the{" "}
                        <Link to={ROUTES.DRILL.MELODIES} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                            Melodies Drill
                        </Link>{" "}
                        and begin training your melodic ear!
                    </p>
                </CalloutBox>
            </ContentSection>

            {/* Practice Strategies */}
            <ContentSection id="practice-strategies">
                <h2 className={styles.sectionTitle}>Effective practice strategies</h2>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>1. Master intervals first</h3>
                <p>
                    Melodic dictation is much easier if you already have solid interval
                    recognition skills. Spend time with the{" "}
                    <Link to={ROUTES.DRILL.INTERVALS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                        Intervals Drill
                    </Link>{" "}
                    before diving deep into melodies.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>2. Start ridiculously simple</h3>
                <p>
                    Begin with just 3-4 scale degrees (1, 2, 3, 5) and 4-note melodies. This might
                    feel too easy, but building confidence and accuracy at this level is crucial.
                    Only add complexity when you're consistently scoring 80%+.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>3. Use the reference note constantly at first</h3>
                <p>
                    Don't be shy about hitting that reference note button repeatedly. Play it
                    before the melody, after the melody, between attempts. Over time, you'll need
                    it less as your internal pitch reference strengthens.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>4. Sing along actively</h3>
                <p>
                    After hearing each melody, sing it back before attempting to identify it. Sing
                    the reference note, then sing the melody. This kinesthetic engagement
                    dramatically improves retention and recognition.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>5. Practice daily in short bursts</h3>
                <p>
                    5-10 minutes every day is ideal for melodic training. Your brain needs regular
                    exposure to develop this skill. Melodic dictation is mentally intensive, so
                    shorter, focused sessions are more effective than marathon practice.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>6. Progress systematically</h3>
                <p>Follow this path:</p>
                <ol style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>4 notes, 3-4 degrees (1, 2, 3, 5), Major scale, Easy difficulty</li>
                    <li>4 notes, all 7 degrees, Major scale, Easy difficulty</li>
                    <li>6 notes, all degrees, Major scale, Easy difficulty</li>
                    <li>6 notes, all degrees, Major scale, Medium difficulty</li>
                    <li>Add Natural Minor scale</li>
                    <li>Increase to 8 notes</li>
                    <li>Finally, add Harmonic Minor scale</li>
                </ol>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>7. Transcribe real songs</h3>
                <p>
                    Apply your skills to actual music! Start with simple children's songs or folk
                    melodies:
                </p>
                <ul style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>"Mary Had a Little Lamb" — 3-2-1-2-3-3-3</li>
                    <li>"Hot Cross Buns" — 3-2-1, 3-2-1</li>
                    <li>"Happy Birthday" — 5-5-6-5-1-7, 5-5-6-5-2-1</li>
                </ul>
                <p>
                    These simple melodies give you real-world context and make the practice feel
                    more musical and rewarding.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>8. Play melodies on your instrument</h3>
                <p>
                    After successfully identifying a melody, play it on your instrument. This
                    reinforces the connection between what you hear, what you think, and what you
                    do physically. It completes the circle of musical understanding.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>9. Don't neglect other skills</h3>
                <p>
                    Melodic dictation benefits from and supports other ear training skills. Keep
                    practicing{" "}
                    <Link to={ROUTES.DRILL.INTERVALS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                        intervals
                    </Link>
                    ,{" "}
                    <Link to={ROUTES.DRILL.CHORDS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                        chords
                    </Link>
                    , and{" "}
                    <Link to={ROUTES.DRILL.PROGRESSIONS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                        progressions
                    </Link>{" "}
                    alongside melodies for well-rounded musicianship.
                </p>

                <CalloutBox>
                    <p>
                        <strong>Remember:</strong> Melodic dictation is perhaps the most difficult
                        ear training skill to master, but also one of the most rewarding. Every
                        musician struggles with it at first. The key is consistent, patient
                        practice. Celebrate every small victory—recognizing even one short melody
                        correctly is real progress. Keep at it, and your ears will open up in ways
                        that transform your musicianship.
                    </p>
                </CalloutBox>

                <div style={{ marginTop: "3rem", padding: "1.5rem", background: "var(--callout-bg)", borderRadius: "8px" }}>
                    <h3 style={{ marginTop: 0 }}>Next Steps</h3>
                    <p style={{ marginBottom: "1rem" }}>
                        You now understand the fundamentals of melodic dictation. Time to train
                        your ears:
                    </p>
                    <Link
                        to={ROUTES.DRILL.MELODIES}
                        style={{
                            display: "inline-block",
                            padding: "0.75rem 1.5rem",
                            background: "var(--accent-color)",
                            color: "white",
                            borderRadius: "6px",
                            textDecoration: "none",
                            fontWeight: "600",
                            marginRight: "1rem",
                        }}
                    >
                        Start Melodies Drill →
                    </Link>
                    <p style={{ marginTop: "1.5rem", marginBottom: 0 }}>
                        Or review the building blocks:{" "}
                        <Link to={ROUTES.LEARN.INTERVALS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                            Intervals
                        </Link>
                        ,{" "}
                        <Link to={ROUTES.LEARN.CHORDS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                            Chords
                        </Link>
                        , and{" "}
                        <Link to={ROUTES.LEARN.PROGRESSIONS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                            Progressions
                        </Link>
                        .
                    </p>
                </div>
            </ContentSection>
        </LearnPageLayout>
        </>
    );
}
